import { getEnvConstant } from 'constants/env';

/**
 * GA4(Google Analytics 4) 연동 매니저.
 *
 * 목적:
 *   기존 핵클(HackleManager)로 보내던 이벤트를 GA4에도 "동일하게" 미러링하기 위한 유틸.
 *   실제 미러링 분기는 HackleManager.logEvent 에서 일어나고, 이 클래스는 GA4(gtag) 전송만 담당한다.
 *
 * 로딩 방식:
 *   별도 npm 패키지(react-ga4 등) 없이 구글 공식 gtag.js 스크립트를 동적으로 주입한다.
 *   외부 SDK를 <script> 태그로 로드하는 기존 프로젝트 컨벤션(kakao/facebook/iamport)과 동일하다.
 *
 * 환경 분기:
 *   측정 ID는 빌드 시점이 아닌 런타임에 window.REACT_APP_ENV 로 결정되므로
 *   getEnvConstant('gaTrackingId') 로 dev/staging/prod 별 ID를 가져와 주입한다.
 *
 * 주의:
 *   호스트 어드민 앱(웹뷰) 내부에서는 네이티브가 GA4 전송을 처리하므로,
 *   이 웹 SDK 경로(window.gtag)는 일반 브라우저 환경에서만 동작한다.
 */
class GAManager {
  /** gtag.js 중복 주입 방지 플래그. */
  private static initialized = false;

  /**
   * gtag.js 스크립트를 동적으로 주입하고 GA4를 초기화한다.
   * 앱 부트스트랩(index.tsx) 시 1회 호출한다. 중복 호출은 무시된다.
   */
  static initialize() {
    if (GAManager.initialized) {
      return;
    }

    const trackingId = getEnvConstant('gaTrackingId');

    // 측정 ID가 비어있는 환경에서는 초기화하지 않는다(방어 코드).
    if (!trackingId) {
      return;
    }

    // 구글 공식 gtag.js 스크립트를 비동기로 로드한다.
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    // gtag 표준 스니펫: dataLayer 에 arguments 를 그대로 push 하는 함수를 전역에 등록한다.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // gtag 는 반드시 arguments 객체를 변형 없이 push 해야 정상 동작한다. (rest 파라미터로 펼치면 안 됨)
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // 초기 설정. send_page_view: false 로 자동 페이지뷰를 끈다.
    // (어드민은 SPA 라우팅이고, 핵클과 동일한 커스텀 이벤트만 수동 전송하기 위함)
    window.gtag('js', new Date());
    window.gtag('config', trackingId, { send_page_view: false });

    GAManager.initialized = true;
  }

  /**
   * 로그인 등으로 유저가 식별될 때 GA4 에 user_id 와 유저 속성을 설정한다.
   * 핵클의 setUserId / setUserProperty 와 짝을 이룬다.
   *
   * @param userId     GA4 user_id 로 설정할 호스트 ID
   * @param properties user_properties 로 설정할 추가 속성(호스트명, 슈퍼호스트여부 등)
   */
  static setUserId(userId: string, properties?: Record<string, any>) {
    // gtag 미초기화 환경(예: 측정 ID 없음)에서는 무시한다.
    if (!window.gtag) {
      return;
    }

    const trackingId = getEnvConstant('gaTrackingId');

    // config 재호출로 user_id 를 갱신한다. (send_page_view 는 계속 off 유지)
    window.gtag('config', trackingId, {
      user_id: userId,
      send_page_view: false,
    });

    if (properties) {
      window.gtag('set', 'user_properties', properties);
    }
  }

  /**
   * 로그아웃 시 GA4 의 user_id 를 해제한다. 핵클의 resetUser 와 짝을 이룬다.
   */
  static reset() {
    if (!window.gtag) {
      return;
    }

    const trackingId = getEnvConstant('gaTrackingId');

    // user_id 를 null 로 설정해 이후 이벤트가 식별된 유저에 귀속되지 않도록 한다.
    window.gtag('config', trackingId, {
      user_id: null,
      send_page_view: false,
    });
  }

  /**
   * GA4 커스텀 이벤트를 전송한다. 핵클의 hackleClient.track 과 동일한 이벤트명/파라미터를 받는다.
   * (웹 브라우저 환경에서만 호출됨 — 웹뷰는 네이티브가 처리)
   *
   * @param eventName 이벤트명 (핵클과 동일한 snake_case 키)
   * @param params    이벤트 파라미터 (핵클과 동일한 객체)
   */
  static logEvent(eventName: string, params?: any) {
    if (!window.gtag) {
      return;
    }

    window.gtag('event', eventName, params || {});
  }
}

export default GAManager;
